// /signup-kine — form de signup público del kinesiólogo.
//
// Flow (B8 — patrón AuthCallback):
//   1. Form: nombre, apellido, email, password (+confirm), matricula,
//      especialidad (libre), provincia (24 AR), 2 checkboxes obligatorios.
//   2. supabase.auth.signUp({email, password, options.data: {...}}) —
//      crea auth.users + manda email de confirmación. Los datos del
//      form viajan en raw_user_meta_data, NO se llama RPC acá.
//   3. Redirect a /signup-kine/email-enviado con email en route state.
//   4. El usuario clickea el link del email → Supabase activa la sesión
//      y redirige a /auth/callback → AuthCallback lee user_metadata y
//      llama create_kine_row con sesión activa → redirige a / (home kine).
//
// Pre-B8 había bug: llamada inline a create_kine_row post-signUp con
// confirm-email ON → auth.uid() null → RPC fallaba con NO_AUTH → cuenta
// queda orphan en auth.users. El patrón AuthCallback lo resuelve porque
// la RPC corre DESPUÉS del email confirm, con sesión activa.
//
// R1: estado='active' por default (auto-aprobar) — aplica en la RPC,
// no se decide acá.

import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase/client";
import { AuthLayout } from "@/app/components/auth/AuthLayout";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";

const PROVINCIAS_AR = [
  "CABA",
  "Buenos Aires",
  "Catamarca",
  "Chaco",
  "Chubut",
  "Córdoba",
  "Corrientes",
  "Entre Ríos",
  "Formosa",
  "Jujuy",
  "La Pampa",
  "La Rioja",
  "Mendoza",
  "Misiones",
  "Neuquén",
  "Río Negro",
  "Salta",
  "San Juan",
  "San Luis",
  "Santa Cruz",
  "Santa Fe",
  "Santiago del Estero",
  "Tierra del Fuego",
  "Tucumán",
];

type Mode = "idle" | "submitting";

export function SignupKine() {
  const navigate = useNavigate();
  const [nombre, setNombre] = useState("");
  const [apellido, setApellido] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [matricula, setMatricula] = useState("");
  const [especialidad, setEspecialidad] = useState("");
  const [provincia, setProvincia] = useState("");
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [declaraMatricula, setDeclaraMatricula] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<Mode>("idle");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Client-side validation
    if (!nombre.trim()) {
      setError("Ingresá tu nombre");
      return;
    }
    if (!apellido.trim()) {
      setError("Ingresá tu apellido");
      return;
    }
    if (!email.trim()) {
      setError("Ingresá tu email");
      return;
    }
    if (password.length < 8) {
      setError("La contraseña tiene que tener al menos 8 caracteres");
      return;
    }
    if (!/[0-9!@#$%^&*(),.?":{}|<>]/.test(password)) {
      setError("La contraseña tiene que incluir al menos 1 número o símbolo");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden");
      return;
    }
    if (!matricula.trim()) {
      setError("Ingresá tu matrícula profesional");
      return;
    }
    if (!provincia) {
      setError("Elegí tu provincia");
      return;
    }
    if (!aceptaTerminos) {
      setError("Tenés que aceptar los términos y código de conducta");
      return;
    }
    if (!declaraMatricula) {
      setError(
        "Tenés que confirmar la declaración bajo responsabilidad civil sobre tu matrícula",
      );
      return;
    }

    setMode("submitting");

    // signUp con metadata: los datos del form viajan en raw_user_meta_data
    // y AuthCallback los consume post email-confirm para llamar
    // create_kine_row con sesión activa.
    const fullName = `${nombre.trim()} ${apellido.trim()}`;
    const { data: signUpData, error: signUpError } =
      await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            role: "kine",
            nombre: fullName,
            matricula: matricula.trim(),
            especialidad: especialidad.trim() || null,
            provincia,
          },
        },
      });

    if (signUpError) {
      const msg = signUpError.message.toLowerCase();
      if (
        msg.includes("already registered") ||
        msg.includes("user already") ||
        msg.includes("already exists")
      ) {
        setError(
          "Ya existe una cuenta con este email. Iniciá sesión o usá otro email.",
        );
      } else if (msg.includes("rate limit") || msg.includes("too many")) {
        setError("Intentaste muchas veces. Esperá unos minutos.");
      } else if (msg.includes("password")) {
        setError(signUpError.message);
      } else {
        setError(signUpError.message);
      }
      setMode("idle");
      return;
    }

    if (!signUpData.user) {
      setError("No pudimos crear tu cuenta. Probá de nuevo.");
      setMode("idle");
      return;
    }

    // Success — redirect a confirmación con el email en state.
    // El linkeo de dominio (create_kine_row) lo hace AuthCallback
    // después del email confirm.
    navigate("/signup-kine/email-enviado", {
      state: { email: email.trim() },
    });
  };

  return (
    <AuthLayout
      title="Crear cuenta de kinesiólogo"
      subtitle="Completá tus datos para empezar a usar Gonix."
      footer={
        <>
          ¿Ya tenés cuenta?{" "}
          <Link
            to="/login"
            className="text-primary hover:underline font-medium"
          >
            Iniciá sesión
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="nombre"
              className="block text-sm font-medium mb-1.5"
            >
              Nombre
            </label>
            <Input
              id="nombre"
              type="text"
              autoComplete="given-name"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              disabled={mode === "submitting"}
            />
          </div>
          <div>
            <label
              htmlFor="apellido"
              className="block text-sm font-medium mb-1.5"
            >
              Apellido
            </label>
            <Input
              id="apellido"
              type="text"
              autoComplete="family-name"
              value={apellido}
              onChange={(e) => setApellido(e.target.value)}
              disabled={mode === "submitting"}
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-1.5">
            Email
          </label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com"
            disabled={mode === "submitting"}
          />
        </div>

        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium mb-1.5"
          >
            Contraseña
          </label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres + 1 número o símbolo"
              disabled={mode === "submitting"}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
              aria-label={
                showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
              }
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        <div>
          <label
            htmlFor="confirm"
            className="block text-sm font-medium mb-1.5"
          >
            Confirmar contraseña
          </label>
          <Input
            id="confirm"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={mode === "submitting"}
          />
        </div>

        <div>
          <label
            htmlFor="matricula"
            className="block text-sm font-medium mb-1.5"
          >
            Matrícula profesional
          </label>
          <Input
            id="matricula"
            type="text"
            value={matricula}
            onChange={(e) => setMatricula(e.target.value)}
            placeholder="ej. MN 12345 o MP-BA 6789"
            disabled={mode === "submitting"}
          />
        </div>

        <div>
          <label
            htmlFor="especialidad"
            className="block text-sm font-medium mb-1.5"
          >
            Especialidad{" "}
            <span className="text-muted-foreground font-normal">(opcional)</span>
          </label>
          <Input
            id="especialidad"
            type="text"
            value={especialidad}
            onChange={(e) => setEspecialidad(e.target.value)}
            placeholder="ej. Traumatología deportiva"
            disabled={mode === "submitting"}
          />
        </div>

        <div>
          <label
            htmlFor="provincia"
            className="block text-sm font-medium mb-1.5"
          >
            Provincia
          </label>
          <select
            id="provincia"
            value={provincia}
            onChange={(e) => setProvincia(e.target.value)}
            disabled={mode === "submitting"}
            className="flex h-9 w-full rounded-md border border-input bg-input-background px-3 py-1 text-sm outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">Elegí tu provincia…</option>
            {PROVINCIAS_AR.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2 pt-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={aceptaTerminos}
              onChange={(e) => setAceptaTerminos(e.target.checked)}
              disabled={mode === "submitting"}
              className="mt-0.5 shrink-0"
            />
            <span className="text-xs text-muted-foreground leading-relaxed">
              Acepto los términos, política de privacidad y código de conducta
              profesional de Gonix.
            </span>
          </label>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={declaraMatricula}
              onChange={(e) => setDeclaraMatricula(e.target.checked)}
              disabled={mode === "submitting"}
              className="mt-0.5 shrink-0"
            />
            <span className="text-xs text-muted-foreground leading-relaxed">
              Declaro bajo responsabilidad civil que la matrícula que ingreso
              es real, vigente y de mi propiedad.
            </span>
          </label>
        </div>

        {error && (
          <div
            role="alert"
            className="text-sm rounded-md px-3 py-2 bg-red-50 text-red-900 border border-red-200"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          className="w-full"
          disabled={mode === "submitting"}
        >
          {mode === "submitting" ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Creando cuenta…
            </>
          ) : (
            "Crear cuenta"
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}
